import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { getArticleBySlug, getRecentArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = getRecentArticles(4).filter((a) => a.slug !== slug);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - 一起草每日大赛" description="抱歉，您访问的文章不存在。" />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-200 rounded-full">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被删除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.publishDate,
      image: article.image,
      publisher: {
        '@type': 'Organization',
        name: '一起草每日大赛',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://williamallen.cn' },
        { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://williamallen.cn/category/${article.category}` },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: article.title,
      description: article.excerpt,
      url: `https://williamallen.cn/article/${article.slug}`,
    },
  ];

  return (
    <>
      <SEOHead
        title={`${article.title} - 一起草每日大赛`}
        description={article.excerpt}
        keywords={`一起草每日大赛,${article.tags.join(',')},${article.categoryName},草类竞技`}
        canonicalUrl={`https://williamallen.cn/article/${article.slug}`}
        ogType="article"
        articleData={{
          publishedTime: article.publishDate,
          author: article.author,
        }}
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full max-w-4xl mx-auto">
          <Breadcrumb />

          <article className="px-4 md:px-6 pb-16">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {article.categoryName}
                </Link>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-200 text-foreground-500 rounded-full text-xs font-medium whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读 {article.readTime}
                </span>
              </div>
            </header>

            <figure className="mb-10 -mx-4 md:mx-0">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-64 md:h-96 object-cover object-top rounded-none md:rounded-xl"
              />
            </figure>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回{article.categoryName}
              </Link>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="px-4 md:px-6 pb-16">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group flex gap-4 bg-background-100 rounded-xl p-4 hover:bg-background-200/80 transition-all cursor-pointer"
                  >
                    <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold text-foreground-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500">{ra.publishDate} · {ra.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}